import { memo, useCallback, useRef } from 'react';
import { ActionIcon, Divider, FileButton, Group, Tooltip } from '@mantine/core';
import {
  IconBlockquote,
  IconBold,
  IconCode,
  IconH1,
  IconH2,
  IconH3,
  IconItalic,
  IconLineDashed,
  IconLink,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconStrikethrough,
  IconUnderline,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/react';
import styles from './TiptapToolbar.module.css';

interface TiptapToolbarProps {
  editor: Editor;
  onImageUpload?: (file: File) => Promise<string>;
}

const TOOLBAR_ICON_SIZE = 18;
const TOOLBAR_BUTTON_SIZE = 30;
const TOOLBAR_BUTTON_RADIUS = 'sm-md';
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

export const TiptapToolbar = memo(function TiptapToolbar({
  editor,
  onImageUpload,
}: TiptapToolbarProps) {
  const { t } = useTranslation();
  const fileButtonResetRef = useRef<() => void>(null);

  const handleImageSelect = useCallback(
    async (file: File | null) => {
      if (!file || !onImageUpload) return;
      try {
        const url = await onImageUpload(file);
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch {
        // Error handled by onImageUpload callback
      }
      fileButtonResetRef.current?.();
    },
    [editor, onImageUpload]
  );

  const handleLinkToggle = useCallback(() => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt(t('notes.editor.enterUrl'));
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor, t]);

  return (
    <div className={styles.toolbar}>
      <Group gap={2} wrap="nowrap" className={styles.toolbarGroup}>
        <Tooltip label={t('notes.editor.bold')}>
          <ActionIcon
            variant={editor.isActive('bold') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <IconBold size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.italic')}>
          <ActionIcon
            variant={editor.isActive('italic') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <IconItalic size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.underline')}>
          <ActionIcon
            variant={editor.isActive('underline') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <IconUnderline size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.strikethrough')}>
          <ActionIcon
            variant={editor.isActive('strike') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <IconStrikethrough size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>

        <Divider orientation="vertical" className={styles.divider} />

        <Tooltip label={t('notes.editor.heading1')}>
          <ActionIcon
            variant={
              editor.isActive('heading', { level: 1 }) ? 'filled' : 'subtle'
            }
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <IconH1 size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.heading2')}>
          <ActionIcon
            variant={
              editor.isActive('heading', { level: 2 }) ? 'filled' : 'subtle'
            }
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <IconH2 size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.heading3')}>
          <ActionIcon
            variant={
              editor.isActive('heading', { level: 3 }) ? 'filled' : 'subtle'
            }
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <IconH3 size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>

        <Divider orientation="vertical" className={styles.divider} />

        <Tooltip label={t('notes.editor.bulletList')}>
          <ActionIcon
            variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <IconList size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.orderedList')}>
          <ActionIcon
            variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <IconListNumbers size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.blockquote')}>
          <ActionIcon
            variant={editor.isActive('blockquote') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <IconBlockquote size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('notes.editor.codeBlock')}>
          <ActionIcon
            variant={editor.isActive('codeBlock') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <IconCode size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>

        <Divider orientation="vertical" className={styles.divider} />

        <Tooltip label={t('notes.editor.link')}>
          <ActionIcon
            variant={editor.isActive('link') ? 'filled' : 'subtle'}
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={handleLinkToggle}
          >
            <IconLink size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>

        {onImageUpload && (
          <FileButton
            resetRef={fileButtonResetRef}
            onChange={handleImageSelect}
            accept={IMAGE_ACCEPT}
          >
            {(buttonProps) => (
              <Tooltip label={t('notes.editor.image')}>
                <ActionIcon
                  variant="subtle"
                  size={TOOLBAR_BUTTON_SIZE}
                  radius={TOOLBAR_BUTTON_RADIUS}
                  {...buttonProps}
                >
                  <IconPhoto size={TOOLBAR_ICON_SIZE} />
                </ActionIcon>
              </Tooltip>
            )}
          </FileButton>
        )}

        <Tooltip label={t('notes.editor.horizontalRule')}>
          <ActionIcon
            variant="subtle"
            size={TOOLBAR_BUTTON_SIZE}
            radius={TOOLBAR_BUTTON_RADIUS}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <IconLineDashed size={TOOLBAR_ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </div>
  );
});
